/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { usePlayerSettings } from "./PlayerSettingsContext";
import { PlayerController, PlayerTimer } from "./types";

type PlayerTimerContextValue = {
  timer: PlayerTimer;
  timeForProgress: (progress: number) => number;
};

type PlayerTimerProviderProps = {
  player: PlayerController;
  children: ReactNode;
};

const PlayerTimerContext = createContext<PlayerTimerContextValue>(
  {} as PlayerTimerContextValue,
);

const DEFAULT_TIMER: PlayerTimer = {
  currentTime: 0,
  duration: 0,
  liveDuration: 0,
  progress: 0,
  isLive: false,
  atLiveEdge: false,
  isInfinityStream: false,
};

export const PlayerTimerProvider = (props: PlayerTimerProviderProps) => {
  const { player, children } = props;

  const [timer, setTimer] = useState<PlayerTimer>({ ...DEFAULT_TIMER });
  const mountRef = useRef<boolean>(false);

  const {
    playbackRate: { defaultRate },
  } = usePlayerSettings();

  const calculateProgress = useCallback(() => {
    const currentTime = player.currentTime();

    if (player.isLive()) {
      if (player.atLiveEdge()) {
        return 1;
      }

      return Math.abs(
        (currentTime - player.liveSeekableStart()) / player.liveWindow(),
      );
    } else {
      const duration = player.duration();

      return duration === Infinity ? 1 : currentTime / duration;
    }
  }, [player]);

  const timeForProgress = useCallback(
    (progress: number): number => {
      return player.isLive()
        ? player.liveSeekableStart() + progress * player.liveWindow()
        : progress *
            (player.duration() === Infinity
              ? player.liveCurrentTime()
              : player.duration());
    },
    [player],
  );

  const onTimeUpdate = useCallback(() => {
    if (mountRef.current) {
      const isLive = player.isLive();
      const currentTime = player.currentTime();
      const duration = player.duration();
      const liveCurrentTime = player.liveCurrentTime();
      const playerAtLiveEdge = player.atLiveEdge();

      const isInfinityDuration = duration === Infinity;
      const isInfinityStream = !isLive && isInfinityDuration;

      setTimer({
        duration,
        currentTime,
        isLive,
        liveDuration: liveCurrentTime,
        progress: calculateProgress(),
        atLiveEdge: Boolean(
          currentTime && (playerAtLiveEdge || isInfinityStream),
        ),
        isInfinityStream,
      });

      // Reset playback rate to default when hitting current live time
      if (
        isLive &&
        currentTime > liveCurrentTime &&
        player.playbackRate() > defaultRate
      ) {
        player.playbackRate(defaultRate);
      }
    }
  }, [calculateProgress, player, defaultRate]);

  useEffect(() => {
    const unsubscribe = player.onEvent(
      ["timeupdate", "durationchange", "ended"],
      onTimeUpdate,
    );

    return () => unsubscribe();
  }, [onTimeUpdate, player]);

  useEffect(() => {
    mountRef.current = true;

    return () => {
      mountRef.current = false;
    };
  }, []);

  return (
    <PlayerTimerContext.Provider value={{ timer, timeForProgress }}>
      {children}
    </PlayerTimerContext.Provider>
  );
};

export const usePlayerTimer = (): PlayerTimerContextValue => {
  const context = useContext(PlayerTimerContext);

  if (!context) {
    throw new Error("Component beyond PlayerTimerContext");
  }

  return context;
};
